import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { PrimaryButton } from "@components/Buttons";
import { AccessCell, MatrixLegend } from "./PermissionMatrixSection";
import {
  getFullPermissionMatrix,
  PERMISSION_MATRIX_COLUMNS,
} from "@constants/permissionAccessMatrix";

const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "[href]",
  'input:not([disabled])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[role="button"]:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

const COLUMN_LABEL_KEYS = {
  MANAGER: "role.permission-matrix.columns.MANAGER",
  PRODUCT_CREATOR: "role.permission-matrix.columns.PRODUCT_CREATOR",
  PRODUCT_MANAGER: "role.permission-matrix.columns.PRODUCT_MANAGER",
  PROJECT_MANAGER: "role.permission-matrix.columns.PROJECT_MANAGER",
  DEVELOPER: "role.permission-matrix.columns.DEVELOPER",
  LICENSE_POLICY_EDIT: "role.permission-matrix.columns.LICENSE_POLICY_EDIT",
  VULNERABILITY_POLICY_EDIT: "role.permission-matrix.columns.VULNERABILITY_POLICY_EDIT",
  SBOM_EDIT: "role.permission-matrix.columns.SBOM_EDIT",
};

/**
 * Header icon popup: overview of all permissions + full comparison matrix.
 */
const PermissionGuidePopup = ({
  "data-testid": dataTestId = "permission-guide-popup",
}) => {
  const { t, i18n } = useTranslation();
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview"); // overview | matrix
  const triggerRef = useRef(null);
  const dialogRef = useRef(null);
  const titleId = useId();

  const matrixGroups = useMemo(() => getFullPermissionMatrix(), []);

  const overviewItems = useMemo(
    () =>
      PERMISSION_MATRIX_COLUMNS.map((code) => {
        const summaryKey = `role.permission-descriptions.${code}.summary`;
        return {
          code,
          label: t(COLUMN_LABEL_KEYS[code], code),
          summary: i18n.exists(summaryKey) ? t(summaryKey) : "",
        };
      }),
    [t, i18n],
  );

  const closePopup = useCallback(() => {
    setIsOpen(false);
    setActiveTab("overview");
    requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const focusFrame = requestAnimationFrame(() => {
      dialogRef.current
        ?.querySelector(`[data-testid="${dataTestId}-close-button"]`)
        ?.focus();
    });

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        event.preventDefault();
        closePopup();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [isOpen, closePopup, dataTestId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const openPopup = (e) => {
    e.stopPropagation();
    setIsOpen(true);
  };

  const tabClass = (tab) =>
    `px-3 py-1.5 text-sm rounded-md transition-colors ${
      activeTab === tab
        ? `${colors.bgIconAccent} ${colors.textActive} font-semibold`
        : `${colors.textSecondary} ${colors.hoverTextNeutral}`
    }`;

  const popup = isOpen
    ? createPortal(
        <div
          role="presentation"
          className={`fixed inset-0 ${colors.bgBlurModal} z-[60] flex items-center justify-center p-4 backdrop-blur-[2px]`}
          data-testid={`${dataTestId}-overlay`}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className={`relative w-full max-w-6xl max-h-[90vh] flex flex-col ${colors.bgModal} border ${colors.borderPrimary} rounded-xl shadow-2xl overflow-hidden`}
            data-testid={dataTestId}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`h-1 w-full shrink-0 ${colors.buttonBackgroundGradient}`} />

            <div className={`flex items-start gap-3 px-6 pt-5 pb-3 border-b ${colors.borderPrimary} shrink-0`}>
              <div className="min-w-0 flex-1">
                <h3 id={titleId} className={`text-lg font-semibold ${colors.textPrimary}`}>
                  {t("role.permission-guide-title", "Permission guide")}
                </h3>
                <p className={`mb-0 mt-1 text-sm ${colors.textSecondary}`}>
                  {t(
                    "role.permission-guide-description",
                    "Overview of each permission and a full access matrix across modules.",
                  )}
                </p>
              </div>
            </div>

            <div
              className={`flex gap-2 px-6 pt-3 shrink-0`}
              role="tablist"
              aria-label={t("role.permission-guide-title", "Permission guide")}
              data-testid={`${dataTestId}-tabs`}
            >
              <button
                type="button"
                role="tab"
                id={`${dataTestId}-tab-overview`}
                aria-selected={activeTab === "overview"}
                aria-controls={`${dataTestId}-panel-overview`}
                tabIndex={activeTab === "overview" ? 0 : -1}
                className={tabClass("overview")}
                onClick={() => setActiveTab("overview")}
                data-testid={`${dataTestId}-tab-overview`}
              >
                {t("role.permission-guide-tab-overview", "Overview")}
              </button>
              <button
                type="button"
                role="tab"
                id={`${dataTestId}-tab-matrix`}
                aria-selected={activeTab === "matrix"}
                aria-controls={`${dataTestId}-panel-matrix`}
                tabIndex={activeTab === "matrix" ? 0 : -1}
                className={tabClass("matrix")}
                onClick={() => setActiveTab("matrix")}
                data-testid={`${dataTestId}-tab-matrix`}
              >
                {t("role.permission-guide-tab-matrix", "Full matrix")}
              </button>
            </div>

            <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0" data-testid={`${dataTestId}-content`}>
              {activeTab === "overview" ? (
                <div
                  role="tabpanel"
                  id={`${dataTestId}-panel-overview`}
                  aria-labelledby={`${dataTestId}-tab-overview`}
                  className="grid grid-cols-1 md:grid-cols-2 gap-3"
                  data-testid={`${dataTestId}-overview`}
                >
                  {overviewItems.map((item) => (
                    <div
                      key={item.code}
                      className={`rounded-lg border ${colors.borderPrimary} ${colors.bgInput} px-4 py-3`}
                      data-testid={`${dataTestId}-overview-${item.code}`}
                    >
                      <p className={`mb-1 text-sm font-semibold ${colors.textActive}`}>{item.label}</p>
                      <p className={`mb-0 text-sm leading-relaxed ${colors.textPrimary}`}>{item.summary}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  role="tabpanel"
                  id={`${dataTestId}-panel-matrix`}
                  aria-labelledby={`${dataTestId}-tab-matrix`}
                  className="space-y-3"
                  data-testid={`${dataTestId}-full-matrix`}
                >
                  <div className={`rounded-lg border ${colors.borderPrimary} overflow-hidden`}>
                    <div className="max-h-[28rem] overflow-auto">
                      <table className="w-full border-collapse text-xs min-w-[64rem]">
                        <thead>
                          <tr>
                            <th
                              className={`px-2 py-2 text-left font-semibold ${colors.textPrimary} sticky top-0 left-0 z-[3] ${colors.bgPrimaryDarker} w-32 min-w-32 border-r ${colors.borderPrimary}`}
                            >
                              {t("role.permission-matrix.col-module", "Module")}
                            </th>
                            <th
                              className={`px-2 py-2 text-left font-semibold ${colors.textPrimary} sticky top-0 left-32 z-[3] ${colors.bgPrimaryDarker} w-28 min-w-28 border-r ${colors.borderPrimary}`}
                            >
                              {t("role.permission-matrix.col-action", "Action")}
                            </th>
                            {PERMISSION_MATRIX_COLUMNS.map((code) => (
                              <th
                                key={code}
                                className={`px-2 py-2 text-center font-semibold ${colors.textPrimary} whitespace-nowrap sticky top-0 z-[2] ${colors.bgPrimaryDarker}`}
                              >
                                {t(COLUMN_LABEL_KEYS[code], code)}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {matrixGroups.map(({ group, rows }) =>
                            rows.map((row, rowIndex) => (
                              <tr key={row.id} className={`border-t ${colors.borderPrimary}`}>
                                {rowIndex === 0 ? (
                                  <td
                                    rowSpan={rows.length}
                                    className={`px-2 py-1.5 align-top font-medium ${colors.textActive} sticky left-0 z-[1] ${colors.bgModal} w-32 min-w-32 border-r ${colors.borderPrimary}`}
                                  >
                                    {t(`role.permission-matrix.groups.${group}`, group)}
                                  </td>
                                ) : null}
                                <td
                                  className={`px-2 py-1.5 ${colors.textPrimary} sticky left-32 z-[1] ${colors.bgModal} w-28 min-w-28 border-r ${colors.borderPrimary}`}
                                >
                                  {t(`role.permission-matrix.features.${row.feature}`, row.feature)}
                                </td>
                                {PERMISSION_MATRIX_COLUMNS.map((code) => (
                                  <td key={code} className="px-2 py-1.5 text-center align-middle">
                                    <div className="inline-flex justify-center">
                                      <AccessCell access={row.cells[code]} t={t} colors={colors} />
                                    </div>
                                  </td>
                                ))}
                              </tr>
                            )),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <MatrixLegend
                    colors={colors}
                    t={t}
                    compact
                    dataTestId={`${dataTestId}-matrix-legend`}
                  />
                </div>
              )}
            </div>

            <div className={`flex justify-end px-6 py-4 border-t ${colors.borderPrimary} shrink-0`}>
              <PrimaryButton type="button" size="md" onClick={closePopup} data-testid={`${dataTestId}-close-button`}>
                {t("common.close-button")}
              </PrimaryButton>
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-testid={`${dataTestId}-trigger`}
        className={`ml-1.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${colors.textComparisonLink} ${colors.hoverTextComparisonLink} focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-blue-400`}
        aria-label={t("role.permission-guide-aria", "View all permission descriptions")}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={openPopup}
      >
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {popup}
    </>
  );
};

export default PermissionGuidePopup;
